import { Directive, ElementRef, HostListener, OnInit, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
    selector: 'input[appAmountFormat]',
    standalone: true,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AmountFormatDirective),
            multi: true
        }
    ]
})
export class AmountFormatDirective implements ControlValueAccessor, OnInit {
    private onChange: (value: number | null) => void = () => {};
    private onTouched: () => void = () => {};

    constructor(private el: ElementRef<HTMLInputElement>) {}

    ngOnInit(): void {
        this.el.nativeElement.type = 'text';
        this.el.nativeElement.inputMode = 'decimal';
    }

    writeValue(value: number | null): void {
        if (value === null || value === undefined) {
            this.el.nativeElement.value = '';
        } else {
            this.el.nativeElement.value = this.formatNumber(value);
        }
    }

    registerOnChange(fn: (value: number | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    @HostListener('input', ['$event'])
    onInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        let raw = input.value;

        // Remove everything except digits and dot
        raw = raw.replace(/[^0-9.]/g, '');

        // Ensure only one decimal point
        const parts = raw.split('.');
        if (parts.length > 2) {
            raw = parts[0] + '.' + parts.slice(1).join('');
        }

        // Limit to 2 decimal places while typing
        if (parts.length === 2 && parts[1].length > 2) {
            raw = parts[0] + '.' + parts[1].substring(0, 2);
        }

        // Update the input with cleaned value (no commas while typing)
        input.value = raw;

        // Emit numeric value to form control
        const numericValue = raw === '' ? null : parseFloat(raw);
        this.onChange(numericValue !== null && isNaN(numericValue) ? null : numericValue);
    }

    @HostListener('focus')
    onFocus(): void {
        const raw = this.parseFormatted(this.el.nativeElement.value);
        this.el.nativeElement.value = raw !== null ? String(raw) : '';
    }

    @HostListener('blur')
    onBlur(): void {
        this.onTouched();
        const raw = this.parseFormatted(this.el.nativeElement.value);
        if (raw !== null) {
            this.el.nativeElement.value = this.formatNumber(raw);
            this.onChange(raw);
        } else {
            this.el.nativeElement.value = '';
            this.onChange(null);
        }
    }

    @HostListener('keydown', ['$event'])
    onKeyDown(event: KeyboardEvent): void {
        const allowed = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Home', 'End'];
        if (allowed.includes(event.key)) return;

        // Allow Ctrl/Cmd + A, C, V, X
        if ((event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase())) return;

        // Allow digits and one decimal point
        if (event.key === '.' && !this.el.nativeElement.value.includes('.')) return;
        if (event.key >= '0' && event.key <= '9') return;

        event.preventDefault();
    }

    private formatNumber(value: number): string {
        return value.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }

    private parseFormatted(value: string): number | null {
        if (!value) return null;
        const cleaned = value.replace(/,/g, '');
        const num = parseFloat(cleaned);
        return isNaN(num) ? null : num;
    }
}
